/**
 * Bank routing (ABA) and deposit-account validation.
 *
 * Mirrored from `commercial-v3-backend/src/utils/bank.ts` so a value the UI accepts is a value the
 * API accepts. Keep the two in sync when you change a rule.
 */

/** Keep the digits only, capped at the 9 an ABA number has. */
export function normalizeRoutingNumber(value: any): string {
  return `${value ?? ''}`.replace(/\D/g, '').slice(0, 9)
}

/**
 * The first two digits are the Federal Reserve routing symbol. Only these ranges are ever issued,
 * so anything else is a typo rather than a bank we have not heard of.
 */
function hasValidRoutingPrefix(routingNumber: string): boolean {
  const prefix = Number(routingNumber.slice(0, 2))
  return (
    prefix <= 12 || // Federal Reserve banks and US Government
    (prefix >= 21 && prefix <= 32) || // thrift institutions
    (prefix >= 61 && prefix <= 72) || // electronic transaction identifiers
    prefix === 80 // traveler's cheques
  )
}

/** The ABA check digit: weights 3-7-1 repeating across all nine digits, total must be a multiple of 10. */
export function isValidRoutingChecksum(routingNumber: string): boolean {
  if (!/^\d{9}$/.test(routingNumber)) return false
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1]
  const sum = weights.reduce((total, weight, index) => total + weight * Number(routingNumber[index]), 0)
  return sum % 10 === 0
}

/**
 * `''` when the value can be saved, otherwise the message to show. A blank value is left to the
 * field's own `required` rule - this only judges values somebody actually typed.
 */
export function validateRoutingNumber(value: any): string {
  const raw = `${value ?? ''}`.trim()
  if (!raw) return ''
  const routingNumber = normalizeRoutingNumber(raw)
  if (routingNumber.length !== 9) return 'Routing number must be 9 digits.'
  if (/^0+$/.test(routingNumber) || !hasValidRoutingPrefix(routingNumber)) return 'Not a valid routing number.'
  if (!isValidRoutingChecksum(routingNumber)) return 'Routing number failed its check digit.'
  return ''
}

export function isValidRoutingNumber(value: any): boolean {
  return !validateRoutingNumber(value) && normalizeRoutingNumber(value).length === 9
}

/** NACHA gives the deposit account number 17 positions. */
export const BANK_ACCOUNT_NUMBER_MAX_LENGTH = 17
export const BANK_ACCOUNT_NUMBER_MIN_LENGTH = 4

/** A value the API handed back masked (`****1234`) is a placeholder, not something to re-validate. */
export function isMaskedAccountNumber(value: any): boolean {
  return /^\*+[0-9a-zA-Z]*$/.test(`${value ?? ''}`.trim())
}

export function normalizeBankAccountNumber(value: any): string {
  const raw = `${value ?? ''}`.trim()
  if (isMaskedAccountNumber(raw)) return raw
  return raw.replace(/[^0-9a-zA-Z-]/g, '').slice(0, BANK_ACCOUNT_NUMBER_MAX_LENGTH)
}

/**
 * Banks differ far too much to check an account number against a format, so this rejects only what
 * can never settle: characters no ODFI accepts, and lengths outside the NACHA field.
 */
export function validateBankAccountNumber(value: any): string {
  const raw = `${value ?? ''}`.trim()
  if (!raw) return ''
  if (isMaskedAccountNumber(raw)) return ''
  if (/[^0-9a-zA-Z-]/.test(raw)) return 'Account number can only contain letters, numbers and dashes.'
  if (raw.length > BANK_ACCOUNT_NUMBER_MAX_LENGTH)
    return `Account number cannot be longer than ${BANK_ACCOUNT_NUMBER_MAX_LENGTH} characters.`
  const digitsAndLetters = raw.replace(/-/g, '')
  if (digitsAndLetters.length < BANK_ACCOUNT_NUMBER_MIN_LENGTH)
    return `Account number must be at least ${BANK_ACCOUNT_NUMBER_MIN_LENGTH} characters.`
  if (/^0+$/.test(digitsAndLetters)) return 'Not a valid account number.'
  return ''
}

export function isValidBankAccountNumber(value: any): boolean {
  return !validateBankAccountNumber(value)
}
